/*
 * Author: Harper Yan
 * File Created: Tuesday, 24-07-09 04:25 pm
 * Last Modified: Tuesday, 25-06-10 10:32 am, by Dw
 */
import type { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import axios from 'axios';
import { IncomingMessage } from 'http';
import { NextRequest } from 'next/server';
import { v4 as uuidV4 } from 'uuid';

import { collectDeviceInfo, cookies, encryptRequest } from '@web/utils';

import type { SuccessResponse } from '@/http.d';

import { logger as baseLogger } from './logger';

export { createRetryableLoginHandler } from './pendingRequestQueue';
export type { SuccessResponse, HttpError } from '@/http.d';
export type { AxiosError } from 'axios';

// Middleware 请求对象格式化
export const fmtMiddlewareReq = (request: NextRequest) => {
  return {
    headers: Object.fromEntries(request.headers.entries()),
    cookies: Object.fromEntries(
      request.cookies.getAll().map((c) => [c.name, c.value])
    ),
  };
};

export type CustomAxiosRequestConfigReq = Partial<IncomingMessage> & {
  cookies?: Partial<{
    [key: string]: string;
  }>;
  headers?:
    | {
        [k: string]: string;
      }
    | unknown;
};

export enum ErrorAction {
  /** 【 DEFAULT 】，会走后续的处理逻辑 */
  CONTINUE = 'CONTINUE',
  /** 忽略后续处理逻辑，并直接返回后端数据 */
  RETURN = 'RETURN',
  /** 忽略后续处理逻辑，并直接返回 rejected 错误 */
  HALT = 'HALT',
}

export type CustomAxiosRequestConfig<RR extends boolean = false> =
  AxiosRequestConfig & {
    hideError?: boolean;
    /** 当需要对该接口调用的异常进行定制化逻辑处理时使用，返回值行为见 ErrorAction 的说明 */
    onError?: (
      error: SuccessResponse<any> & { status: number } & { locale: string }
    ) => Promise<void | ErrorAction> | void | ErrorAction;
    cacheConfig?: {
      /** 是否使用缓存请求后的结果 */
      useCache?: boolean;
      /** 生效时长，不填则代表永久，单位ms - 设置缓存的时候生效  */
      maxAge?: number;
    };
    req?: Partial<IncomingMessage> & {
      cookies: Partial<{
        [key: string]: string;
      }>;
    };

    // 是否需要返回原始的响应数据
    rawResponse?: RR;

    /** 是否需要把传输参数加密 */
    security?: {
      /** 公钥 */
      publicKey: string;
      customHeaders?: (params: {
        /** 本次随机 iv */
        ivBase64: string;
        /** 本次随机加密 key */
        keyBase64: string;
        /** 本次随机签名key */
        signKeyBase64: string;
      }) => Record<string, string>;
      customBody?: (params: {
        /** 本次随机 iv */
        ivBase64: string;
        /** 本次随机加密 key */
        keyBase64: string;
        /** 本次随机签名key */
        signKeyBase64: string;
        /** 加密后的数据 */
        encryptedData: string;
      }) => Record<string, any>;
    };
  };

export const rejectIgnoreSentry = <T extends object>(data: T) => {
  return Promise.reject({
    ...data,
    ignoreSentry: true,
  });
};

/**
 * @description 接口请求返回数据缓存
 * {
 *    [path]: {
 *        [JSON.stringify(params)]: {
 *            // 生效时长，不填则代表永久，单位ms
 *            maxAge: number,
 *            // 返回数据类型
 *            data: ResponseData(any)
 *         }
 *    }
 * }
 */
const cacheMap: Record<
  string,
  Record<
    string,
    {
      startTimestamp?: number;
      maxAge?: number;
      data: any;
    }
  >
> = {};

const uniqParams = (params?: any) =>
  params ? JSON.stringify(params) : 'EMPTY';

const checkCache = (c: {
  cacheConfig?: CustomAxiosRequestConfig['cacheConfig'];
  url: string;
  params?: any;
}) => {
  const { cacheConfig, url, params } = c;
  if (cacheConfig?.useCache && url) {
    // 是否需要在cache里面寻找
    let needFind = true;
    const cacheData = cacheMap[url]?.[uniqParams(params)];
    if (cacheData?.startTimestamp && cacheData.maxAge) {
      // 缓存的时间
      const expiredTime = cacheData.maxAge + cacheData.startTimestamp;
      const currentTime = Date.now();
      if (expiredTime < currentTime) {
        needFind = false;
      }
    }
    if (needFind && cacheData) {
      return cacheData.data;
    }
  }
};

const addCache = <R = any>(p: {
  url: string;
  params?: any;
  cacheConfig?: CustomAxiosRequestConfig['cacheConfig'];
  data: R;
}) => {
  const { url, cacheConfig, data, params } = p;
  if (cacheConfig && url) {
    const hasExpired = !!cacheConfig.maxAge;

    cacheMap[url] = {
      ...cacheMap[url],
      [uniqParams(params)]: {
        startTimestamp: hasExpired ? Date.now() : undefined,
        maxAge: hasExpired ? cacheConfig.maxAge : undefined,
        data,
      },
    };
  }
};

export type GlobalErrorParams = SuccessResponse<any> & {
  status: number;
  locale?: string;
};

const THIRD_PARTY_HOSTS = [
  'https://anycross-sg.larksuite.com',
  process.env.NEXT_PUBLIC_THIRD_API_HOST,
  process.env.NEXT_PUBLIC_LARK_HOST,
];

let _onGlobalLogin:
  | ((
      instance: AxiosInstance,
      error: any
    ) => Promise<AxiosResponse<any, any> | ErrorAction | void>)
  | undefined;
let _onGlobalError:
  | ((
      error: GlobalErrorParams
    ) => Promise<void | ErrorAction> | void | ErrorAction)
  | undefined;

export const setAxiosCallBack = (functions: {
  onLogin?: typeof _onGlobalLogin;
  onError?: (
    error: SuccessResponse<any> & {
      status: number;
      locale?: string;
    }
  ) => Promise<void | ErrorAction> | void | ErrorAction;
}) => {
  _onGlobalLogin = functions.onLogin;
  _onGlobalError = functions.onError;
};

const dynamicBaseURL = () => {
  if (typeof window === 'undefined') {
    // 服务端
    return process.env.NODE_ENV === 'development'
      ? process.env.PRIVATE_API_HOST
      : process.env.PRIVATE_API_HOST;
  }
  return '/';
};

/**
 * 实例化 并配置
 */
export const instance = axios.create({
  baseURL: dynamicBaseURL(),
  timeout: 10000,
  headers: {
    // this is the default header configuration, can be overwritten in each request
    'content-type': 'application/json',
    'X-Client-Type': 'web',
    'Accept-Language': 'en',
  },
});

instance.interceptors.request.use(async (config) => {
  if (config) config.headers['x-trace-id'] = uuidV4();

  const NEXT_LOCALE = cookies.get('NEXT_LOCALE', config.headers.cookie);
  const fingerprint = cookies.get('fingerprint', config.headers.cookie);

  config.headers['Accept-Language'] = NEXT_LOCALE || 'en';

  if (fingerprint) {
    config.headers['Fingerprint'] = fingerprint;
  }

  config.headers['device-info'] = await collectDeviceInfo(
    typeof window === 'undefined'
  );

  // for server side
  if (
    typeof window === 'undefined' &&
    (config as CustomAxiosRequestConfig).req
  ) {
    const cookie = (config as CustomAxiosRequestConfig)?.req?.cookies || {};
    // 将 cookies 对象转换为 cookie 字符串
    const cookieString = Object.entries(cookie)
      .map(([key, value]) => `${key}=${value}`)
      .join('; ');

    if (cookieString) {
      config.headers['Cookie'] = cookieString;
    }

    const headers = (config as CustomAxiosRequestConfig)?.req?.headers;
    if (headers?.['x-gray-env']) {
      config.headers['x-gray-env'] = headers?.['x-gray-env'];
    }
  }

  return config;
});

export const handleErrorAction = async (
  error: any,
  action: ErrorAction | unknown | AxiosResponse
) => {
  if (action === ErrorAction.RETURN) {
    return { data: error?.response?.data };
  } else if (action === ErrorAction.HALT) {
    return rejectIgnoreSentry(error?.response?.data);
  }
  return;
};

instance.interceptors.response.use(
  async (res: AxiosResponse<SuccessResponse<any>>) => {
    // 处理三方请求 (dcs, lark)
    if (
      THIRD_PARTY_HOSTS.some(
        (host) =>
          host &&
          (res.config.url?.startsWith(host) || res.config.baseURL === host)
      )
    ) {
      return res ?? {};
    }

    const reqConfig = res.config as typeof res.config &
      CustomAxiosRequestConfig;

    const locale = reqConfig.headers['Accept-Language'];

    // Process successful responses
    if (res.data.success || reqConfig?.hideError) {
      return res;
    }

    if (reqConfig.onError) {
      const actionEnum =
        (await reqConfig.onError?.({
          ...res.data,
          status: res.status,
          locale,
        })) || ErrorAction.CONTINUE;
      if (actionEnum === ErrorAction.RETURN) {
        return res;
      } else if (actionEnum === ErrorAction.HALT) {
        return rejectIgnoreSentry(res.data);
      }
    }

    if (_onGlobalError) {
      const actionEnum =
        (await _onGlobalError?.({ ...res.data, status: res.status, locale })) ||
        ErrorAction.CONTINUE;
      if (actionEnum === ErrorAction.RETURN) {
        return res;
      } else if (actionEnum === ErrorAction.HALT) {
        return rejectIgnoreSentry(res.data);
      }
    }

    return rejectIgnoreSentry(res.data);
  },
  async (error) => {
    if (error.config?.rawResponse && typeof window === 'undefined') {
      return Promise.reject(error);
    }

    let logger = baseLogger;

    const reqConfig = error?.config as typeof error.config &
      CustomAxiosRequestConfig;

    const cookieHeader = error.config?.headers['cookie'];
    const locale = error.config?.headers['Accept-Language'];

    if (error.config && error.config?.headers) {
      const fingerprint = cookies.get('fingerprint', cookieHeader);
      logger = baseLogger.clone({ fingerprint });
    }

    if (error?.response?.status === 401) {
      if (_onGlobalLogin) {
        const actionEnum =
          (await _onGlobalLogin(instance, error)) || ErrorAction.CONTINUE;

        if ((actionEnum as AxiosResponse<any, any>)?.status) {
          return actionEnum;
        }

        const result = await handleErrorAction(error, actionEnum);
        if (result) return result;
      }
    }

    // 排除请求取消的 axios 错误弹窗
    if (error.code !== 'ECONNABORTED' && !reqConfig?.hideError) {
      logger.error('HTTP REQUEST ERROR', error);

      if (reqConfig.onError) {
        const actionEnum =
          (await reqConfig.onError?.({
            ...error?.response?.data,
            status: error?.response?.status,
            locale,
          })) || ErrorAction.CONTINUE;
        const result = await handleErrorAction(error, actionEnum);
        if (result) return result;
      }

      if (_onGlobalError) {
        const actionEnum =
          (await _onGlobalError({
            ...error?.response?.data,
            status: error?.response?.status,
            locale,
          })) || ErrorAction.CONTINUE;
        const result = await handleErrorAction(error, actionEnum);
        if (result) return result;
      }
    }

    return rejectIgnoreSentry({
      code: error?.response?.data?.code,
      status: error.status,
    });
  }
);

/**
 * get
 * @param url [string]
 * @param params [object] path query params
 * @param config [CustomAxiosRequestConfig] Http configuration per request
 * @returns async VoidFunction
 */
export const get = <T, R = SuccessResponse<T>, RR extends boolean = false>(
  url: string,
  params?: Record<string, any>,
  config?: CustomAxiosRequestConfig<RR>
): Promise<RR extends true ? AxiosResponse<R> : R> => {
  const cacheRes = checkCache({
    url,
    params,
    cacheConfig: config?.cacheConfig,
  });

  if (cacheRes && !config?.rawResponse) {
    return Promise.resolve(cacheRes);
  }

  return instance.get<R>(url, { params, ...config }).then((res) => {
    if (config?.rawResponse) {
      return res as any;
    }

    // 如果 R 是 SuccessResponse<T> 类型，则返回 res.data，否则直接返回 res.data.data
    if ((res?.data as SuccessResponse<T>)?.success !== undefined) {
      addCache({
        url,
        data: res.data,
        params,
        cacheConfig: config?.cacheConfig,
      });
      return res.data;
    }
    addCache({
      url,
      data: res?.data,
      params,
      cacheConfig: config?.cacheConfig,
    });
    return res?.data; // 直接返回数据，不进行处理
  });
};

/**
 * post
 * @param url [string]
 * @param data [Object] Post body payload
 * @param config [CustomAxiosRequestConfig] Http configuration per request
 * @returns async VoidFunction
 */
export const post = <T, R = SuccessResponse<T>, RR extends boolean = false>(
  url: string,
  data?: any,
  config?: CustomAxiosRequestConfig<RR>
): Promise<R> => {
  const cacheRes = checkCache({
    url,
    params: data,
    cacheConfig: config?.cacheConfig,
  });
  if (cacheRes && !config?.rawResponse) {
    return Promise.resolve(cacheRes as R);
  }

  const headers = config?.headers || {};

  if (config?.security) {
    const {
      randomSignKey,
      xSign,
      encryptionBody,
      encryptionRandomKey,
      time,
      ivBase64,
    } = encryptRequest({
      data,
      publicKey: config.security.publicKey,
    });

    let tmpHeaders: Record<string, any> = {
      'x-iv': ivBase64,
      'x-nonce': randomSignKey,
      'x-signature': xSign,
      'x-timestamp': time,
    };
    let tmpBody: Record<string, any> = {
      encryptionBody,
      encryptionRandomKey,
    };

    tmpHeaders =
      config.security.customHeaders?.({
        ivBase64,
        keyBase64: encryptionRandomKey,
        signKeyBase64: xSign,
      }) ?? tmpHeaders;
    tmpBody =
      config.security.customBody?.({
        ivBase64,
        keyBase64: encryptionRandomKey,
        signKeyBase64: xSign,
        encryptedData: encryptionBody,
      }) ?? tmpBody;

    Object.assign(headers, tmpHeaders);
    data = tmpBody;
  }

  return instance.post<R>(url, data, { ...config, headers }).then((res) => {
    if (config?.rawResponse) {
      return res as any;
    }

    if ((res?.data as SuccessResponse<T>)?.success !== undefined) {
      addCache({
        url,
        data: res.data,
        params: data,
        cacheConfig: config?.cacheConfig,
      });
      return res.data;
    }
    addCache({
      url,
      data: res?.data,
      params: data,
      cacheConfig: config?.cacheConfig,
    });
    return res?.data as unknown as R; // 直接返回数据，不进行处理
  });
};

/**
 * put
 * @param url [string]
 * @param data [Object] Post body payload
 * @param config [CustomAxiosRequestConfig] Http configuration per request
 * @returns async VoidFunction
 */
export const put = <T>(
  url: string,
  data?: any,
  config?: CustomAxiosRequestConfig
) => {
  return instance
    .put<SuccessResponse<T>>(url, data, config)
    .then((res) => res?.data);
};

/**
 * delete
 * @param url [string]
 * @param data [Object] Post body payload
 * @returns async VoidFunction
 */
export const del = <T>(
  url: string,
  params?: Record<string, any>,
  config?: CustomAxiosRequestConfig
) => {
  return instance
    .delete<SuccessResponse<T>>(url, { params, ...config })
    .then((res) => res?.data);
};

/**
 * 清除接口缓存结果
 * @param url 请求地址
 */
export const clearCache = (url: string) => {
  delete cacheMap[url];
};

export { default as _axios } from 'axios';
export { type AxiosResponse } from 'axios';
